/**
 * "What this decision rests on" (Drivers programme, OR-30.47): the same panel under every decision's control, driven by `DRIVERS`. Three parts, each with its basis: what you have now (the figures at the round start),
 * what it produced in each year played (all of them, the status-quo twin muted under each figure), and what the draft's setting adds against the no-change plan (the preview's figure for this decision, else "Shown after this round").
 * The Cockpit's grammar: a name, one figure, a muted basis line.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useBand } from "../../theme/breakpoints";
import type { ContentCatalog, DecisionCatalogEntry, TeamState } from "@bsim/contracts";
import { GxLoading } from "../../theme/GxLoading";
import { GxNotes } from "../../theme/GxNotes";
import { GxPill } from "../../theme/GxPill";
import { GxTable, type GxColumn, type GxRow } from "../../theme/GxTable";
import { usePreviewRead } from "../../shell/PreviewProvider";
import { decisionIdOf, readDecision, sameValue, settingPath, type SettingValue } from "../decisionValues";
import { ProgrammeTable } from "../preview/ProgrammeTable";
import { stripOf } from "../preview/costProgramme";
import { useRoundPosition } from "../useRoundPosition";
import { CARD_PRICED, decisionAccess, type DecisionAccess } from "../decisionAccess";
import { stepFigures } from "./optionFigures";
import { DRIVERS, SHOWN_LATER, nowOf, readYear, type DriverField, type DriverYear } from "./drivers";
import { useDriverYears } from "./useDriverYears";
import "./drivers.css";

export const BACKCAST_NOTE = "The years before the game are worked back from your starting position in line with demand growth; they are not recorded history.";
const NOTHING_PLAYED = "Nothing has been played yet. Each year you play adds a column here.";


/** Part two: one column per year played, the twin's figure muted under each. The unit of a figure is in its row's basis line (the rows differ), so the cells hold the driver's own reading. */
export function YearsTable({ id, fields, years, position, nowLabel }: { readonly id: string; readonly fields: readonly DriverField[]; readonly years: readonly DriverYear[]; readonly position: Parameters<typeof nowOf>[1]; readonly nowLabel: string }) {
  // A matrix of years: whenever its box is narrower than the years played it scrolls inside that box with the driver name pinned (the grid mode answers for the box, never the window).
  const columns: GxColumn[] = [
    { key: "driver", label: "Driver" },
    ...years.map((y): GxColumn => ({ key: `y${y.roundNo}`, label: y.label, kind: "number", header: <>{y.label}{y.before === true ? <small className="gx-faint"> before the game</small> : null}</> })),
    { key: "now", label: nowLabel, kind: "number", highlight: true },
  ];
  const rows: GxRow[] = fields.map((f) => {
    const cells: Record<string, ReactNode> = { driver: <>{f.label}{f.read === null ? null : <small className="gx-faint">{f.basis}</small>}</> };
    if (f.read === null) {
      cells.now = <span className="gx-drv-later" data-testid={`drivers.${id}.years.${f.id}.later`}>{SHOWN_LATER}</span>;
    } else {
      for (const y of years) {
        const r = readYear(f, y);
        cells[`y${y.roundNo}`] = (
          <span data-testid={`drivers.${id}.years.${f.id}.${y.roundNo}`}>
            <span>{r.you === null ? "–" : f.show(r.you)}</span>
            {r.twin === null ? null : <small className="gx-faint gx-drv-twin">Twin {f.show(r.twin)}</small>}
          </span>
        );
      }
      cells.now = <span data-testid={`drivers.${id}.years.${f.id}.now`}>{(() => { const v = nowOf(f, position, years); return v === null ? "–" : f.show(v); })()}</span>;
    }
    return { key: f.id, cells };
  });
  return <GxTable testId={`drivers.${id}.years`} caption="What each year produced; Twin is your status-quo twin, the muted figure under yours" columns={columns} rows={rows} dense phone="grid" />;
}

const UPDATING = "Updating…";
const NEXT_STEP_NOTE = "Figures are shown for your pick, your standing policy setting and the next step each way. A dash means that setting is not priced here: move your pick to it to see its figures.";
const VALUE_NOTE = "Value is in dollars a share against the no-change plan.";
const TEAM_NOTE: ReadonlySet<string> = new Set(["loans.ci.growth", "loans.cre.growth", "loans.resi.growth"]);
const PAY_NOTE = "Cost is the change in the year's operating expense against the no-change plan: the bankers' pay and hiring cost, and the running cost of the lending they write. A team shrinks no faster than people leave, so shrinking and stopping hires can end the year with the same bankers; stopping hires also stops lending beyond renewals, which is where their costs differ.";

/**
 * The decision's options as a leader would put them to you: one row per setting, what it costs this year, what it adds to the driver and what it is worth a share, against the no-change plan. The pick is the editor's live
 * setting (not the saved one); the figures are the engine's for the draft, the standing policy and one step either side (`stepFigures`), and the other settings print "–".
 */
function OptionsTable({ entry, team }: { readonly entry: DecisionCatalogEntry; readonly team: TeamState }) {
  const feed = usePreviewRead();
  const id = entry.decisionId;
  const sub = Object.keys(entry.options)[0] ?? "";
  const options = entry.options[sub] ?? [];
  const leaf = settingPath(id, sub);
  const stepOf = feed?.stepOf;
  useEffect(() => (stepOf === undefined ? undefined : stepOf(leaf)), [stepOf, leaf]);
  const draft = feed?.draft ?? team.draft;
  const pick = draft === null ? undefined : readDecision(draft.decisions, id, sub);
  const standing = draft === null ? undefined : readDecision(draft.seedDecisions, id, sub);
  const rows = feed?.trace?.decisions.filter((d) => decisionIdOf(d.decisionId) === id) ?? [];
  const held = pick !== undefined && sameValue(pick, standing);
  const own = held ? 0 : rows.length > 0 && rows.every((r) => r.valueUsdPerShare !== undefined) ? rows.reduce((t, r) => t + (r.valueUsdPerShare ?? 0), 0) : null;
  const labelOf = (v: SettingValue | undefined): string | undefined => options.find((o) => v !== undefined && sameValue(o.value, v))?.label;
  const figures = stepFigures(id, feed?.steps?.decisionId === leaf ? feed.steps : null, labelOf(pick), labelOf(standing), own);
  // A figure priced on the previous draft is never shown: until the run on the saved draft lands, every cell says so. A dash is only "not priced for this setting".
  const updating = feed?.state.status !== "ready";
  const programme = id === "operations.expense" && feed?.state.status === "ready" ? feed.state.result.costProgramme : undefined;
  return (
    <section className="gx-drv-part" data-testid={`drivers.${id}.next`}>
      <h3 className="gx-drv-h">What each setting would add</h3>
      <div className="gx-drv-opts">
      <div aria-live="polite">
      <GxTable testId={`drivers.${id}.options`} dense caption="The settings on offer: what each costs this year, what it adds to the driver, and what it is worth a share against the no-change plan"
        columns={[{ key: "setting", label: "Setting" }, { key: "cost", label: id === "operations.expense" ? "One-off charge, this year" : "Cost, this year", kind: "number", phone: "line" }, { key: "adds", label: "Adds to the driver", kind: "number", phone: "line" }, { key: "value", label: "Value, $ a share", kind: "number", phone: "lead" }]}
        rows={options.map((o): GxRow => {
          const picked = sameValue(pick, o.value), kept = sameValue(standing, o.value);
          const f = figures.byLabel.get(o.label);
          const key = String(o.value);
          return {
            key, selected: picked, testId: `drivers.${id}.option.${key}`,
            cells: {
              setting: <>{o.label}{picked ? <> <GxPill tone="gold">Your pick</GxPill></> : null}{kept ? <> <GxPill tone="neutral">Standing policy</GxPill></> : null}</>,
              cost: <span data-testid={`drivers.${id}.option.${key}.cost`}>{updating ? UPDATING : f?.cost ?? "–"}</span>,
              adds: <span data-testid={`drivers.${id}.option.${key}.adds`}>{updating ? UPDATING : f?.adds ?? "–"}</span>,
              value: <span data-testid={picked ? `drivers.${id}.next.value` : `drivers.${id}.option.${key}.value`}>{updating ? UPDATING : f?.value ?? "–"}</span>,
            },
          };
        })} />
      </div>
      </div>
      <GxNotes testId={`drivers.${id}.options.note`} notes={[NEXT_STEP_NOTE, VALUE_NOTE, TEAM_NOTE.has(id) ? PAY_NOTE : null]} />
      {programme === undefined ? null : <div data-testid={`drivers.${id}.next.programme`}><ProgrammeTable programme={programme} testId={`drivers.${id}.next.table`} compact /><GxNotes notes={[`Pays back in ${stripOf(programme).payback.replace(/\.$/, "")}`]} /></div>}
    </section>
  );
}

/** A decision a lock or a card answer holds equals its standing plan in the engine, so no other setting is priced: the panel says that, and where a card hold is priced. The hold itself is read on the control above. */
function HeldPart({ id, card }: { readonly id: string; readonly card: boolean }) {
  return (
    <section className="gx-drv-part" data-testid={`drivers.${id}.held`}>
      <h3 className="gx-drv-h">What each setting would add</h3>
      <p className="gx-small gx-muted">No other setting is priced while this one is held.{card ? ` The hold is ${CARD_PRICED}, not here.` : ""}</p>
    </section>
  );
}

/** `access` is the page's own reading of the hold (it follows an answer just given); without it the panel reads the team's draft. */
export function DriverPanel({ entry, team, catalog, access }: { readonly entry: DecisionCatalogEntry; readonly team: TeamState; readonly catalog: ContentCatalog; readonly access?: DecisionAccess }) {
  const descriptor = DRIVERS[entry.decisionId];
  const roundNo = team.round?.roundNo ?? 0;
  const position = useRoundPosition(team.team.id, roundNo);
  const { years, pending, failed } = useDriverYears(team, catalog);
  // On a phone the panel is a disclosure, closed, whose summary carries the driver's now-figure: nothing the decision needs is hidden behind it.
  const phone = useBand() === "phone";
  const [open, setOpen] = useState(false);
  if (descriptor === undefined) return null;
  const id = entry.decisionId;
  const reading = access ?? decisionAccess(entry, team, false);
  const held = reading.kind === "constrained" ? reading : null;
  const sentence = entry.driver ?? "";
  const lead = descriptor.years.find((f) => f.read !== null);
  const leadNow = lead === undefined ? null : nowOf(lead, position, years);
  const panel = (
    <section className="gx-drv" data-testid={`drivers.${id}`} aria-label="What this decision rests on">
      <div className="gx-drv-top"><div className="gx-eyebrow" role="heading" aria-level={2}>What this decision rests on</div><span className="gx-drv-name">{descriptor.title}</span></div>
      <section className="gx-drv-part"><h3 className="gx-drv-h">What it produced each year</h3>
        {pending ? <GxLoading variant="inline" words="Loading the years played…" /> : failed ? <p className="gx-small gx-muted" data-testid={`drivers.${id}.failed`}>The years played are not available right now.</p> : <YearsTable id={id} fields={descriptor.years} years={years} position={position} nowLabel="Now" />}
        {pending || failed ? null : <>
          <GxNotes testId={`drivers.${id}.backcast`} notes={[years.some((y) => y.before === true) ? BACKCAST_NOTE : null]} />
          <GxNotes testId={`drivers.${id}.none`} notes={[years.every((y) => y.before === true) ? NOTHING_PLAYED : null]} />
        </>}
      </section>
      {held === null ? <OptionsTable entry={entry} team={team} /> : <HeldPart id={id} card={held.card} />}
      {sentence === "" ? null : <p className="gx-drv-s gx-faint" data-testid={`drivers.${id}.sentence`}>{sentence}</p>}
    </section>
  );
  if (!phone) return panel;
  return (
    <div className="gx-drv-disc" data-open={open} data-testid={`drivers.${id}.disclosure`}>
      <button type="button" className="gx-drv-disc-t" aria-expanded={open} data-testid={`drivers.${id}.toggle`} onClick={() => setOpen(!open)}>
        <b>What this decision rests on</b>
        <span>{lead === undefined || leadNow === null ? descriptor.title : `${lead.label} ${lead.show(leadNow)}`} <span aria-hidden="true">{open ? "\u25BE" : "\u25B8"}</span></span>
      </button>
      {open ? panel : null}
    </div>
  );
}
